import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AnalyticsSettingsMenu from "../AnalyticsSettingsMenu";
import useIsMobile from "../../hooks/useIsMobile";
import { mobileDialogPaperSx } from "../../theme/mobileLayout";
import { adjustSubscriptionPlan, getSubscriptionPlanCatalog } from "../../api/subscriptionApi";
import {
  clearPricingRequirement,
  getOnboardingAccountType,
  getRecommendedPlanId,
} from "../../helper/pricingHandoff";
import { resolvePlanTier } from "../../helper/planTierMapping";
import { errorDetail } from "../../helper/errorDetail";
import { BILLING_ANNUAL, PRICING_PLANS, getCatalogPrice } from "./plans";
import BillingCycleToggle from "./BillingCycleToggle";
import PricingPlanCard from "./PricingPlanCard";
import {
  PRICING_VERSION_V1,
  readPricingVersion,
} from "./pricingVersion";

const DEFAULT_CHECKOUT_ERROR =
  "Unable to start checkout. Please choose a plan and try again.";

const getCheckoutErrorMessage = (response) => {
  if (response?.status === 403) {
    return errorDetail(response?.data?.detail, "Only the account owner can manage subscriptions.");
  }
  if (response?.status === 409) {
    return errorDetail(response?.data?.detail, "This subscription cannot be changed right now.");
  }
  if (response?.status === 422) {
    return errorDetail(response?.data?.detail, "Invalid plan selection. Please choose a plan and try again.");
  }
  if (response?.status === 502) {
    return errorDetail(response?.data?.detail, "Payment provider unavailable. Please try again later.");
  }
  return errorDetail(response?.data?.detail, DEFAULT_CHECKOUT_ERROR);
};

/**
 * Mandatory post-onboarding pricing modal that starts hosted Stripe Checkout.
 * Not dismissible while the pricing requirement remains active.
 */
export default function PricingSubscriptionModal({
  open,
  dismissible = false,
  copyMode = "onboarding",
  onClose,
}) {
  const isMobile = useIsMobile();
  const isTrialConversion = copyMode === "trial-conversion";
  const accountType = isTrialConversion ? null : getOnboardingAccountType();
  const recommendedPlanId = useMemo(
    () => isTrialConversion ? null : getRecommendedPlanId(accountType),
    [accountType, isTrialConversion]
  );

  const [billingCycle, setBillingCycle] = useState(BILLING_ANNUAL);
  const [checkoutPlanId, setCheckoutPlanId] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [visualVersion] = useState(() => readPricingVersion());
  const checkoutInFlightRef = useRef(false);
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(false);
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setCatalog(null);
    setCatalogError(false);
    if (open) {
      getSubscriptionPlanCatalog().then((response) => {
        if (!active) return;
        if (response?.status === 200 && response.data?.catalog_version && Array.isArray(response.data.plans)) {
          setCatalog(response.data);
          setCatalogError(response.data.plans.length === 0);
        } else setCatalogError(true);
      }).catch(() => { if (active) setCatalogError(true); });
    }
    return () => { active = false; };
  }, [open, catalogAttempt]);
  const plans = PRICING_PLANS.map((plan) => {
    const entry = catalog?.plans.find((item) => item.tier_id === resolvePlanTier(plan.id));
    return {...plan, prices: entry?.prices, includedSeats: entry?.entitlements?.included_seats,
      seatPrices: catalog?.extra_seat?.prices};
  });

  const handleSelectPlan = useCallback(
    async (plan) => {
      if (checkoutInFlightRef.current || !getCatalogPrice(plan, billingCycle)) return;

      checkoutInFlightRef.current = true;
      setCheckoutPlanId(plan.id);
      setCheckoutError(null);

      try {
        const tierId = resolvePlanTier(plan.id);
        const response = await adjustSubscriptionPlan(tierId, billingCycle);
        const checkoutUrl = response?.data?.checkout_url;

        if (
          response?.status !== 200 ||
          typeof checkoutUrl !== "string" ||
          !checkoutUrl
        ) {
          setCheckoutError(getCheckoutErrorMessage(response));
          return;
        }

        // The onboarding-only gate is not an entitlement boundary. Clearing it
        // before hosted navigation prevents a successful payer from being
        // trapped by the non-dismissible modal on the Stripe return leg.
        clearPricingRequirement();
        window.location.assign(checkoutUrl);
      } catch (_error) {
        setCheckoutError(DEFAULT_CHECKOUT_ERROR);
      } finally {
        checkoutInFlightRef.current = false;
        setCheckoutPlanId(null);
      }
    },
    [billingCycle]
  );

  const handleDialogClose = (_event, reason) => {
    if (!dismissible && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    if (dismissible) onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      fullScreen={isMobile}
      maxWidth={false}
      aria-labelledby="pricing-subscription-title"
      data-testid="pricing-subscription-modal"
      data-dismissible={String(dismissible)}
      disableEscapeKeyDown={!dismissible}
      PaperProps={{
        sx: {
          ...mobileDialogPaperSx(isMobile, { maxWidth: 920 }),
          bgcolor: "#FFFFFF",
          boxShadow: isMobile ? "none" : "0 16px 40px rgba(17, 24, 39, 0.16)",
          ...(isMobile
            ? null
            : {
                m: { xs: 1.5, sm: 2 },
                maxHeight: "calc(100dvh - 48px)",
                width: "100%",
                maxWidth: 920,
                borderRadius: "12px",
              }),
        },
      }}
      BackdropProps={{
        sx: {
          bgcolor: "rgba(17, 24, 39, 0.55)",
        },
      }}
      sx={{
        zIndex: 2600,
      }}
    >
      <DialogContent
        sx={{
          px: { xs: 2, md: 2.5 },
          py: { xs: 2, md: 2.5 },
          pb: { xs: "calc(16px + env(safe-area-inset-bottom, 0px))", md: 2.5 },
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {dismissible ? (
          <IconButton
            type="button"
            aria-label="Close pricing"
            data-testid="pricing-subscription-close"
            onClick={() => onClose?.()}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
        <Box data-testid="pricing-step" data-pricing-version={visualVersion}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              mb: 2.25,
              gap: 1.25,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                id="pricing-subscription-title"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: 18, md: 20 },
                  lineHeight: 1.2,
                  color: "#111827",
                }}
              >
                {isTrialConversion ? "Continue with a paid plan" : "Choose your plan"}
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 12.5,
                  lineHeight: 1.4,
                  color: "#6B7280",
                }}
              >
                {isTrialConversion
                  ? "Choose a subscription to keep using metered features."
                  : "Choose a paid subscription plan to continue."}
              </Typography>
            </Box>

            <BillingCycleToggle
              value={billingCycle}
              onChange={setBillingCycle}
            />
            <AnalyticsSettingsMenu />
          </Box>

          {!catalog && !catalogError ? <Typography role="status">Loading current prices…</Typography> : null}
          {catalogError ? <Alert severity="warning" action={<Button onClick={() => setCatalogAttempt((value) => value + 1)}>Retry</Button>}>
            Current prices are unavailable. Please try again.
          </Alert> : null}
          {checkoutError ? (
            <Alert
              severity="error"
              data-testid="pricing-checkout-error"
              sx={{ mb: 2 }}
            >
              {checkoutError}
            </Alert>
          ) : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: { xs: 1.5, md: 1.75 },
              alignItems: "stretch",
            }}
          >
            {plans.map((plan) => (
              <PricingPlanCard
                key={plan.id}
                plan={plan}
                billingCycle={billingCycle}
                recommended={plan.id === recommendedPlanId}
                onSelect={handleSelectPlan}
                disabled={checkoutPlanId !== null || !getCatalogPrice(plan, billingCycle)}
                visualVersion={visualVersion || PRICING_VERSION_V1}
                offerLabel="Paid subscription"
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
